import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/_share/_services/user.service';

/** A user's roles, tolerating either the new `roles` list or the older
 * single `role` field (still populated by the backend for compat). */
function rolesOf(user: any): { id: number; name?: string }[] {
  if (Array.isArray(user?.roles) && user.roles.length > 0) return user.roles;
  return user?.role ? [user.role] : [];
}

export type PermissionStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Loads the current user's permission names from the backend and exposes
 * synchronous and reactive helpers for checking access.
 *
 * Wires itself to UserService.currentUser$ automatically — no manual
 * initialisation is needed. Just inject the service.
 *
 * Superadmin bypass: a user whose role name is 'superadmin' always receives
 * `true` from hasPermission(), regardless of what is assigned in the DB.
 *
 * Dev warning: in non-production builds, checking a permission name that isn't
 * in the loaded set emits a console.warn — helpful when adding new permission
 * strings before they've been created in the backend.
 *
 * Usage in templates: *appHasPermission="'workforce.view'"
 * Usage in code:      authz.hasPermission('workforce.view')
 * Guard:              route.data['requiredPermission'] = 'workforce.view'
 *                     (guard waits for ready$ before evaluating)
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private _permissions$ = new BehaviorSubject<Set<string>>(new Set());
  private _status$ = new BehaviorSubject<PermissionStatus>('idle');
  private _currentRoleNames: Set<string> = new Set();

  /** Emits the current set of permission names whenever they change. */
  readonly permissions$ = this._permissions$.asObservable();

  /** Emits the current load status. */
  readonly status$ = this._status$.asObservable();

  /**
   * Emits exactly once when the permission load cycle completes (either
   * 'loaded' or 'error'). Guards use this to avoid racing against the HTTP call.
   */
  readonly ready$: Observable<PermissionStatus> = this._status$.pipe(
    filter((s) => s === 'loaded' || s === 'error'),
    take(1)
  );

  constructor(private userService: UserService) {
    this.userService.currentUser$
      .pipe(
        distinctUntilChanged((a, b) => {
          const idsOf = (u: any) =>
            rolesOf(u)
              .map((r) => r.id)
              .sort()
              .join(',');
          return idsOf(a) === idsOf(b);
        })
      )
      .subscribe((user) => {
        const roles = rolesOf(user);
        if (roles.length === 0) {
          this._currentRoleNames = new Set();
          this._permissions$.next(new Set());
          // 'loaded', not 'idle': this is a definitive result (the user
          // genuinely has no roles/permissions), not "haven't started
          // loading yet". ready$ only resolves on 'loaded'/'error' - a
          // route gated by requiredPermission for a roleless user would
          // otherwise hang on ready$ forever, the same failure mode as the
          // original login stall.
          this._status$.next('loaded');
          return;
        }
        this._currentRoleNames = new Set(roles.map((r) => r.name).filter(Boolean) as string[]);
        // Permissions come straight off /users/me's roles[].permissions -
        // no separate request needed. Previously this called
        // GET /roles/{id}/permissions per role, but that endpoint is
        // superadmin-only on the backend: every other role got a 403 on
        // login, which the global error interceptor treated as "log out",
        // wiping the session moments after it was created.
        const names = roles
          .flatMap((role: any) => (Array.isArray(role.permissions) ? role.permissions : []))
          .map((p: any) => String(p.name));
        this._permissions$.next(new Set(names));
        this._status$.next('loaded');
      });
  }

  /**
   * Returns true if the current user holds the named permission.
   * Superadmin always returns true.
   * In dev builds, warns if the permission name is not in the loaded catalog.
   */
  hasPermission(name: string): boolean {
    if (this._currentRoleNames.has('superadmin')) return true;
    const inSet = this._permissions$.value.has(name);
    if (!environment.production && this._status$.value === 'loaded' && !inSet) {
      console.warn(
        `[AuthorizationService] Unknown permission checked: '${name}'. ` +
          `Ensure this permission exists in the backend and is assigned to the relevant roles.`
      );
    }
    return inSet;
  }

  /** Returns true if the current user has at least one of the listed permissions. */
  hasAnyPermission(names: string[]): boolean {
    if (this._currentRoleNames.has('superadmin')) return true;
    return names.some((n) => this._permissions$.value.has(n));
  }

  /** Returns true if the current user has every listed permission. */
  hasAllPermissions(names: string[]): boolean {
    if (this._currentRoleNames.has('superadmin')) return true;
    return names.every((n) => this._permissions$.value.has(n));
  }

  /** Snapshot of the current permission set as a plain array. */
  getPermissions(): string[] {
    return Array.from(this._permissions$.value);
  }

  /** @deprecated returns one of the current user's role names (order not
   * guaranteed under multi-role) for backward-compat single-role fallback
   * checks. Prefer `hasAnyCurrentRole` or `getCurrentRoleNames`. */
  getCurrentRoleName(): string | null {
    return this._currentRoleNames.values().next().value ?? null;
  }

  /** All role names currently held by the user. */
  getCurrentRoleNames(): string[] {
    return Array.from(this._currentRoleNames);
  }

  /** Returns true if the current user holds any of the given role names. */
  hasAnyCurrentRole(names: string[]): boolean {
    return names.some((n) => this._currentRoleNames.has(n));
  }

  /** Synchronous status snapshot. */
  getStatus(): PermissionStatus {
    return this._status$.value;
  }
}
